import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, Response, shouldRenderMobile, ToastService } from '@philips-emr/hpms-provider-commons';
import { HpmsFormTechnicalVisitAPI } from '@philips-emr/hpms-provider-components';
import { ActionAPI, DeckAPI, DeckValue, FetchAPI, FormAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnClick, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitFormsAliases as Aliases } from '../../codegen/constants/aliases/technical-visit-forms-aliases';
import BaseController from '../BaseController';

import { handleSubmitWithAPIs } from './technical-form-utils';

interface Form {
  nrSeqGroup: number,
  dsGroup: string,
  ieStatus: number,
  editable: string,
  nrSeqForm: number,
}

export interface SubObject {
  code: number;
  description: string;
  ieRequired: string;
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_FORMS })
export default class TechnicalVisiFormsController extends BaseController {

  @Inject(Aliases.FETCH_QUESTIONS)
  private fetchQuestions: FetchAPI;

  @Inject(Aliases.PANEL_APPOINTMENT_DETAILS)
  private panelAppointmentDetails: PanelAPI;

  @Inject(Aliases.PANEL_ADITIONAL)
  private panelAditional: PanelAPI;

  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;

  @Inject(Aliases.DECK_FORMS)
  private deckForms: DeckAPI;

  @Inject(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  private hpmsformTechnicalVisit: HpmsFormTechnicalVisitAPI;

  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;

  @Inject(Aliases.BUTTON_FINISH)
  private buttonFinish: ActionAPI;

  @Inject(Aliases.PANEL_UPLOAD)
  private panelUpload: PanelAPI;

  @Inject(Aliases.TEXT_TITLE)
  private textTitle: TextAPI;

  @OnMount(Aliases.PANEL_UPLOAD)
  onMountFinishButtom(event: MountEvent): void {
    const api = event.target as PanelAPI;
    this.controlVisibilityPanels(api);
  }

  @OnMount(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  onMountQuestions(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
  }

  @OnSelectItem(Aliases.DECK_FORMS)
  onClickCard(event: SelectItemEvent<DeckValue>): void {
    const { nrSeqGroup, dsGroup } = event.item;
    this.formSelected(dsGroup as string, nrSeqGroup as number);
  }

  @OnBeforeFetch(Aliases.FETCH_QUESTIONS)
  onBeforeFetchQuetions(event: FetchEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqGroup, nrSeqVisit, nrSeqForm } = this.context.getBulkData();
    api.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
  }

  @OnAfterFetch([Aliases.FETCH_FORMS, Aliases.FETCH_MENU_MOBILE])
  onAfterFetch(event: FetchEvent): void {
    if (!event.data) return;
    const data = event.data as Form[];
    const { dsGroup, nrSeqGroup } = data[0];
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    this.fetchQuestions.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
    this.context.set({ nrSeqGroup, nrSeqVisit, dsGroup, nrSeqForm });
    this.fetchQuestions.forceFetch();
    this.textTitle.setValue(dsGroup);
    if (shouldRenderMobile()) {
      this.formMenuMobile.setData({ menuMobile: { dsGroup, nrSeqGroup } });
    }
    this.deckForms.selectCard(String(nrSeqGroup));
  }

  @OnAfterFetch(Aliases.FETCH_QUESTIONS)
  onAfterFetchQuestionary(): void {
    const { dsGroup } = this.context.getBulkData() as unknown as Form;
    this.textTitle.setValue(dsGroup);
  }

  @OnMount(Aliases.PANEL_MOBILE)
  onMountPanelMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  @OnMount([Aliases.FETCH_FORMS, Aliases.FETCH_MENU_MOBILE])
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqForm } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqForm });
    api.forceFetch();
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onchange(event: ChangeEvent<{ dsGroup: string, nrSeqGroup: number }>): void {
    this.formSelected(event.value.dsGroup, event.value.nrSeqGroup);
  }

  @OnMount(Aliases.BUTTON_FINISH)
  onMountButtomFinish(event: MountEvent): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    const api = event.target as ActionAPI;
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { ieStatus } = response.body;
        api.setEnabled(ieStatus == 1);
      }
    });
  }

  @OnSubmit(Aliases.FORM_FILES)
  onSubmitAttachments(event: SubmitEvent): void {
    const record = event.record;
    const api = event.target as FormAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const body = { ...record, nrSeqVisit };
    void HttpService.post(Endpoint.POST.TECHNICAL_VISIT_ATTACHMENTS, {}, { body }).then(() => {
      this.fetchAttachments.setParameters({ nrSeqVisit });
      this.fetchAttachments.forceFetch();
      api.setRecord({});
    });
  }

  @OnMount(Aliases.FETCH_ATTACHMENTS)
  onMountFetchAttachements(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnClick(Aliases.BUTTON_FINISH)
  onClickFinish(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.TECHNICAL_VISIT_FINISH, { nrSeqVisit, nrSeqForm }).then(() => {
      this.buttonFinish.setEnabled(false);
      this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
      catchRenderException(() => this.controlVisibilityPanels(this.panelUpload));
      ToastService.open({ level: ToastLevel.SUCCESS, message: MessageService.translate('provider.request-sucessfull') });
    }).catch((resp) => {
      if (resp.body) {
        const { response } = resp.body as unknown as Response;
        ToastService.open({ level: ToastLevel.ERROR, message: response });
      }
    });
  }

  @OnSelectItem(Aliases.DECK_ATTACHMENTS)
  onClickDownloadAttachment(event: SelectItemEvent<DeckValue>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSequence, dsAttachment } = event.item;
    const api = event.target as DeckAPI;
    api.unselectAll();
    ToastService.open({ level: ToastLevel.INFORMATION, message: MessageService.translate('provider.download-initiated') });
    FileService.download(Endpoint.GET.TECHNICAL_VISIT_ATTACHMENT_DOWNLOAD, dsAttachment as string,
      { nrSeqAttachment: nrSequence, nrSeqVisit }, HttpMethod.GET)
      .catch(() => {
        ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.error-finding-file') });
      });
  }

  @OnSubmit(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  public onSubmitForm(event: FormAPI): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    handleSubmitWithAPIs(event, nrSeqVisit, this.fetchQuestions, this.hpmsformTechnicalVisit);
  }

  private controlVisibilityPanels(api: PanelAPI): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { editable } = response.body;
        api.setVisible(editable == 'S');
      }
    });
  }

  private formSelected(dsGroup: string, nrSeqGroup: number): void {

    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();

    catchRenderException(() => {
      if (nrSeqGroup) {
        this.panelAppointmentDetails.setVisible(true);
        this.panelAditional.setVisible(false);
        this.context.set({ nrSeqGroup, nrSeqVisit, dsGroup });
        this.textTitle.setValue(dsGroup);
        this.fetchQuestions.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
        this.fetchQuestions.forceFetch();
        this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
      } else {
        this.panelAppointmentDetails.setVisible(false);
        this.panelAditional.setVisible(true);
      }
    });
  }

  private controlEditing(nrSeqVisit: number, nrSeqForm: number): void {
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { editable } = response.body;
        catchRenderException(() => {
          this.hpmsformTechnicalVisit.setEditing(editable == 'S');
        });
      }
    });
  }
}
